import React from "react";
import { format, startOfMonth, endOfMonth, subMonths, startOfYear } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Popover, Calendar, SimpleSelect, Field, Button } from "@/components/ui";
import { PRESET_DATE_OPTIONS } from "@/components/BulkLeaderboardExportDialog/reports";

interface ReportDateRangeProps {
  date: DateRange | undefined;
  preset: string;
  onDateChange: (date: DateRange | undefined) => void;
  onPresetChange: (preset: string) => void;
  className?: string;
}

export const ReportDateRange: React.FC<ReportDateRangeProps> = ({
  date,
  preset,
  onDateChange,
  onPresetChange,
  className,
}) => {
  const handlePresetChange = (value: string | undefined) => {
    if (!value) return;
    onPresetChange(value);
    const now = new Date();
    switch (value) {
      case "this_month":
        onDateChange({ from: startOfMonth(now), to: now });
        break;
      case "last_month":
        const lastMonth = subMonths(now, 1);
        onDateChange({ from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) });
        break;
      case "two_months_ago":
        const twoMonthsAgo = subMonths(now, 2);
        onDateChange({ from: startOfMonth(twoMonthsAgo), to: endOfMonth(twoMonthsAgo) });
        break;
      case "ytd":
        onDateChange({ from: startOfYear(now), to: now });
        break;
      case "all_time":
        onDateChange({ from: undefined, to: undefined });
        break;
      case "custom":
        if (!date?.from) {
          onDateChange({ from: new Date(new Date().setDate(now.getDate() - 30)), to: now });
        }
        break;
    }
  };

  const handleDateChange = (newDate: DateRange | undefined) => {
    onDateChange(newDate);
    onPresetChange("custom");
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Field className="grid gap-2">
        <div className="flex items-center justify-between">
          <Field.Label>Select Timeframe</Field.Label>
          <SimpleSelect
            items={PRESET_DATE_OPTIONS}
            value={preset}
            onValueChange={handlePresetChange}
            className="font-medium text-primary hover:text-primary/80 transition-colors"
          />
        </div>
        <Popover>
          <Popover.Trigger
            render={
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  preset === "all_time" && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {preset === "all_time" ? (
                  <span>All Time</span>
                ) : date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            }
          />
          <Popover.Content className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={date?.from || new Date()}
              selected={date}
              onSelect={handleDateChange}
              numberOfMonths={2}
            />
          </Popover.Content>
        </Popover>
      </Field>
    </div>
  );
};
